/**
 * SystemUtilities - Admin page for system maintenance and migrations
 */

import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Database,
  Play,
  CheckCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  FileText,
  Zap,
} from 'lucide-react';
import { api } from '../../lib/api';
import { pageStyles } from '../../lib/styles';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';

// ============================================================================
// Types
// ============================================================================

interface MigrationStatus {
  total: number;
  withRef: number;
  withoutRef: number;
}

interface MigrationResult {
  success: boolean;
  migratedCount: number;
  errors: string[];
}

interface BackfillCasesResult {
  success: boolean;
  totalBookingsConsidered: number;
  createdCases: number;
  linkedBookings: number;
  linkedJobs: number;
  errors: string[];
}

type NullCaseIdStatus = {
  bookingsNull: number;
  jobsNull: number;
};

// ============================================================================
// Main Component
// ============================================================================

export default function SystemUtilitiesPage() {
  const [showMigrationConfirm, setShowMigrationConfirm] = useState(false);
  const { success: showSuccess, error: showError } = useToast();

  // Fetch booking references status
  const {
    data: statusResponse,
    isLoading: isLoadingStatus,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: ['admin', 'migrations', 'booking-references', 'status'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: MigrationStatus }>(
        '/api/admin/migrations/booking-references/status'
      );
      return response.data;
    },
  });

  // Fetch case_id linkage status (how many rows still null)
  const {
    data: caseIdStatus,
    isLoading: isLoadingCaseStatus,
    refetch: refetchCaseStatus,
  } = useQuery({
    queryKey: ['admin', 'migrations', 'backfill-cases', 'status'],
    queryFn: async () => {
      const q = encodeURIComponent(
        `SELECT ` +
          `(SELECT count(*) FROM bookings WHERE case_id IS NULL) AS bookingsNull, ` +
          `(SELECT count(*) FROM jobs WHERE case_id IS NULL) AS jobsNull;`
      );
      const response = await api.get<{ success: boolean; data: Array<NullCaseIdStatus> }>(
        `/api/admin/migrations/run-sql/query?q=${q}`
      );
      return response.data?.[0] || { bookingsNull: 0, jobsNull: 0 };
    },
  });

  // Run migration mutation
  const runMigrationMutation = useMutation({
    mutationFn: async (dryRun: boolean) => {
      const response = await api.post<{ success: boolean; data: MigrationResult }>(
        `/api/admin/migrations/booking-references?dryRun=${dryRun}`
      );
      return response.data;
    },
    onSuccess: (data, dryRun) => {
      if (dryRun) {
        showSuccess(`Simulación completada: ${data.migratedCount} reservas serían migradas`);
      } else {
        showSuccess(`Migración completada: ${data.migratedCount} reservas actualizadas`);
        refetchStatus();
      }
      setShowMigrationConfirm(false);
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Error al ejecutar migración');
    },
  });

  // Backfill cases mutation
  const runBackfillCasesMutation = useMutation({
    mutationFn: async (dryRun: boolean) => {
      const url = dryRun
        ? `/api/admin/migrations/backfill-cases?dryRun=true`
        : `/api/admin/migrations/backfill-cases?run=true`;
      const response = await api.post<{ success: boolean; data: BackfillCasesResult; message?: string }>(url);
      return response;
    },
    onSuccess: (res, dryRun) => {
      const result = res.data;
      const msg = dryRun
        ? `Dry run: enlazaría ${result.linkedBookings} reservas y ${result.linkedJobs} trabajos (crearía ${result.createdCases} casos).`
        : `Backfill completo: enlazadas ${result.linkedBookings} reservas y ${result.linkedJobs} trabajos (creados ${result.createdCases} casos).`;

      if (result.errors?.length) {
        showError(`${msg} Errores: ${result.errors.slice(0, 3).join(' | ')}${result.errors.length > 3 ? '…' : ''}`);
      } else {
        showSuccess(msg);
      }

      if (!dryRun) {
        refetchCaseStatus();
      }
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Error al ejecutar backfill');
    },
  });

  const status = statusResponse;
  const needsMigration = status && status.withoutRef > 0;

  const needsCaseBackfill = (caseIdStatus?.bookingsNull || 0) > 0 || (caseIdStatus?.jobsNull || 0) > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className={pageStyles.title}>Utilidades del Sistema</h1>
        <p className={pageStyles.subtitle}>
          Herramientas de mantenimiento y migraciones de datos
        </p>
      </div>

      {/* Migration Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Database className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">Referencias de Reservas</CardTitle>
                <CardDescription>
                  Genera referencias únicas para reservas que no las tienen
                </CardDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refetchStatus()}
              disabled={isLoadingStatus}
            >
              <RefreshCw className={`h-4 w-4 ${isLoadingStatus ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingStatus ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-10 w-32" />
            </div>
          ) : status ? (
            <div className="space-y-4">
              {/* Status Overview */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-xs text-muted-foreground">Total reservas</p>
                  <p className="text-2xl font-bold">{status.total}</p>
                </div>
                <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                  <p className="text-xs text-green-600">Con referencia</p>
                  <p className="text-2xl font-bold text-green-700">{status.withRef}</p>
                </div>
                <div className={`p-3 rounded-lg ${needsMigration ? 'bg-amber-50 border border-amber-200' : 'bg-muted'}`}>
                  <p className={`text-xs ${needsMigration ? 'text-amber-600' : 'text-muted-foreground'}`}>
                    Sin referencia
                  </p>
                  <p className={`text-2xl font-bold ${needsMigration ? 'text-amber-700' : ''}`}>
                    {status.withoutRef}
                  </p>
                </div>
              </div>

              {/* Status Badge */}
              <div className="flex items-center gap-2">
                {needsMigration ? (
                  <>
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Migración requerida
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {status.withoutRef} reservas necesitan referencias
                    </span>
                  </>
                ) : (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Todo actualizado
                  </Badge>
                )}
              </div>

              {/* Actions */}
              {needsMigration && (
                <div className="flex items-center gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => runMigrationMutation.mutate(true)}
                    disabled={runMigrationMutation.isPending}
                  >
                    {runMigrationMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4 mr-2" />
                    )}
                    Simular (Dry Run)
                  </Button>
                  <Button
                    onClick={() => setShowMigrationConfirm(true)}
                    disabled={runMigrationMutation.isPending}
                  >
                    {runMigrationMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4 mr-2" />
                    )}
                    Ejecutar migración
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">Error al cargar estado</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Future Utilities Section */}
      <Card className="opacity-60">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              <Zap className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <CardTitle className="text-lg text-muted-foreground">Más utilidades</CardTitle>
              <CardDescription>
                Próximamente: limpieza de datos, exportaciones masivas, y más
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-xs text-muted-foreground">
            {isLoadingCaseStatus
              ? 'Cargando estado de casos pendientes…'
              : needsCaseBackfill
                ? `Reservas sin case_id: ${caseIdStatus?.bookingsNull ?? 0}, trabajos sin case_id: ${caseIdStatus?.jobsNull ?? 0}`
                : 'Todos los casos están enlazados correctamente.'}
          </div>
          <Button
            variant="outline"
            disabled={!needsCaseBackfill || runBackfillCasesMutation.isPending || isLoadingCaseStatus}
            onClick={() => runBackfillCasesMutation.mutate(true)}
          >
            {runBackfillCasesMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 mr-2" />
            )}
            Simular backfill de casos
          </Button>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={showMigrationConfirm}
        onOpenChange={setShowMigrationConfirm}
        title="¿Ejecutar migración?"
        description={`Esta acción generará referencias únicas para ${status?.withoutRef || 0} reservas que no las tienen. Este proceso es seguro y no elimina ningún dato.`}
        confirmText="Ejecutar"
        cancelText="Cancelar"
        onConfirm={() => runMigrationMutation.mutate(false)}
        loading={runMigrationMutation.isPending}
      />
    </div>
  );
}
